(function () {
  const DRAWER_ID = 'SearchDrawer';
  const TRIGGER_SELECTOR = '[data-search-trigger]';
  const CLOSE_SELECTOR = '[data-search-drawer-close]';
  const INPUT_SELECTOR = '[data-search-drawer-input]';
  const CLEAR_SELECTOR = '[data-search-drawer-clear]';
  const BACKDROP_SELECTOR = '[data-search-drawer-backdrop]';
  const RESULTS_ID = 'PredictiveSearchResults';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const DEBOUNCE_MS = 300;

  let drawer, backdrop, trigger, input, clearBtn, resultsContainer, lastFocused;
  let debounceTimer = null;
  let abortController = null;

  function init() {
    drawer = document.getElementById(DRAWER_ID);
    trigger = document.querySelector(TRIGGER_SELECTOR);
    if (!drawer || !trigger) return;

    backdrop = document.querySelector(BACKDROP_SELECTOR);
    input = drawer.querySelector(INPUT_SELECTOR);
    clearBtn = drawer.querySelector(CLEAR_SELECTOR);
    resultsContainer = document.getElementById(RESULTS_ID);

    // Drawer und Backdrop ans Body-Ende verschieben (inert-Vererbung vom Section-Wrapper vermeiden).
    if (drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }
    if (backdrop && backdrop.parentElement !== document.body) {
      document.body.appendChild(backdrop);
    }

    trigger.addEventListener('click', open);
    const closeBtn = drawer.querySelector(CLOSE_SELECTOR);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);
    document.addEventListener('keydown', handleKeydown);

    if (input) {
      input.addEventListener('input', handleInput);
    }
    if (clearBtn) {
      clearBtn.addEventListener('click', clearInput);
    }
  }

  function open() {
    lastFocused = document.activeElement;
    drawer.classList.add('search-drawer--open');
    drawer.setAttribute('aria-hidden', 'false');
    if (backdrop) backdrop.classList.add('search-drawer__backdrop--visible');
    trigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    setInertOnSiblings(true);

    updateTopOffset();

    requestAnimationFrame(function () {
      if (input) input.focus();
    });
  }

  function close() {
    drawer.classList.remove('search-drawer--open');
    drawer.setAttribute('aria-hidden', 'true');
    if (backdrop) backdrop.classList.remove('search-drawer__backdrop--visible');
    trigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    setInertOnSiblings(false);

    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  function updateTopOffset() {
    // Trust-Bar-Hoehe messen, aber clampen auf nicht-negativ.
    // Wenn weit runter gescrollt wurde, ist getBoundingClientRect().bottom negativ
    // und der Drawer waere oberhalb des Viewports unsichtbar.
    const trustBar = document.querySelector('.trust-bar');
    const rawTop = trustBar ? trustBar.getBoundingClientRect().bottom : 0;
    const top = Math.max(0, rawTop);
    drawer.style.setProperty('--search-drawer-top', top + 'px');
    if (backdrop) backdrop.style.setProperty('--search-drawer-top', top + 'px');
  }

  function setInertOnSiblings(active) {
    Array.prototype.forEach.call(document.body.children, function (el) {
      if (el === drawer || el === backdrop) return;
      if (active) {
        el.setAttribute('inert', '');
      } else {
        el.removeAttribute('inert');
      }
    });
  }

  function handleKeydown(e) {
    if (drawer.getAttribute('aria-hidden') === 'true') return;

    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }

    if (e.key === 'Tab') {
      trapFocus(e);
    }
  }

  function trapFocus(e) {
    const focusables = Array.prototype.filter.call(
      drawer.querySelectorAll(FOCUSABLE),
      function (el) { return !el.closest('[hidden]'); }
    );
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function handleInput(e) {
    const query = e.target.value.trim();

    if (clearBtn) {
      if (query.length > 0) {
        clearBtn.removeAttribute('hidden');
      } else {
        clearBtn.setAttribute('hidden', '');
      }
    }

    if (debounceTimer) clearTimeout(debounceTimer);

    if (query.length === 0) {
      clearResults();
      return;
    }

    debounceTimer = setTimeout(function () {
      fetchSuggestions(query);
    }, DEBOUNCE_MS);
  }

  function fetchSuggestions(query) {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    resultsContainer.setAttribute('aria-busy', 'true');

    const url = '/search/suggest.json?q=' + encodeURIComponent(query) +
                '&resources[type]=product,collection&resources[limit]=4';

    fetch(url, { signal: abortController.signal })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        renderResults(data, query);
        resultsContainer.setAttribute('aria-busy', 'false');
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        console.error('Predictive Search Fehler:', err);
        resultsContainer.setAttribute('aria-busy', 'false');
      });
  }

  function renderResults(data, query) {
    const products = (data.resources && data.resources.results && data.resources.results.products) || [];
    const collections = (data.resources && data.resources.results && data.resources.results.collections) || [];

    // Produkte mit Tag "hide-from-search" ausschliessen, dann auf 4 begrenzen
    const filteredProducts = products.filter(function (product) {
      const tags = product.tags || [];
      return tags.indexOf('hide-from-search') === -1;
    });
    const limitedProducts = filteredProducts.slice(0, 4);
    const limitedCollections = collections.slice(0, 2);

    const hasResults = limitedProducts.length > 0 || limitedCollections.length > 0;

    let html = '<div class="predictive-search__panel">';

    if (hasResults) {
      html += '<div class="predictive-search__inner">';

      if (limitedProducts.length > 0) {
        html += '<section class="predictive-search__column predictive-search__column--products" aria-labelledby="PredictiveSearchProductsHeading">';
        html += '<h2 id="PredictiveSearchProductsHeading" class="predictive-search__heading">Produkte</h2>';
        html += '<ul class="predictive-search__list" role="list">';
        limitedProducts.forEach(function (product) {
          const imageUrl = product.featured_image && product.featured_image.url
            ? product.featured_image.url
            : (product.image || '');
          html += '<li class="predictive-search__item">';
          html += '<a href="' + escapeHtml(product.url) + '" class="predictive-search__product-link">';
          if (imageUrl) {
            html += '<img src="' + escapeHtml(imageUrl) + '" alt="" width="80" height="80" loading="lazy" class="predictive-search__product-image">';
          } else {
            html += '<span class="predictive-search__product-image predictive-search__product-image--placeholder" aria-hidden="true"></span>';
          }
          html += '<div class="predictive-search__product-info">';
          html += '<span class="predictive-search__product-title">' + escapeHtml(product.title) + '</span>';

          // Preis-Block: "ab X,XX €" wenn min < max, sonst "X,XX €"
          const priceMin = parseFloat(product.price_min);
          const priceMax = parseFloat(product.price_max);
          const fallbackPrice = product.price;
          let priceLabel = '';

          if (!isNaN(priceMin) && !isNaN(priceMax) && priceMax > priceMin) {
            priceLabel = 'ab ' + formatPrice(product.price_min);
          } else if (!isNaN(priceMin)) {
            priceLabel = formatPrice(product.price_min);
          } else if (fallbackPrice) {
            priceLabel = formatPrice(fallbackPrice);
          }

          if (priceLabel) {
            html += '<span class="predictive-search__product-price">' + escapeHtml(priceLabel) + '</span>';
          }

          html += '</div>';
          html += '</a>';
          html += '</li>';
        });
        html += '</ul>';
        html += '</section>';
      }

      if (limitedCollections.length > 0) {
        html += '<section class="predictive-search__column predictive-search__column--collections" aria-labelledby="PredictiveSearchCollectionsHeading">';
        html += '<h2 id="PredictiveSearchCollectionsHeading" class="predictive-search__heading">Kategorien</h2>';
        html += '<ul class="predictive-search__list" role="list">';
        limitedCollections.forEach(function (collection) {
          html += '<li class="predictive-search__item">';
          html += '<a href="' + escapeHtml(collection.url) + '" class="predictive-search__collection-link">' + escapeHtml(collection.title) + '</a>';
          html += '</li>';
        });
        html += '</ul>';
        html += '</section>';
      }

      html += '</div>';
    } else {
      html += '<div class="predictive-search__empty">';
      html += '<p>Keine Treffer für „' + escapeHtml(query) + '".</p>';
      html += '</div>';
    }

    html += '<a href="/search?q=' + encodeURIComponent(query) + '" class="predictive-search__view-all">';
    html += 'Nach „' + escapeHtml(query) + '" suchen';
    html += '</a>';

    html += '</div>';

    resultsContainer.innerHTML = html;
  }

  function formatPrice(value) {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(num);
  }

  function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function clearInput() {
    if (input) {
      input.value = '';
      input.focus();
    }
    if (clearBtn) clearBtn.setAttribute('hidden', '');
    clearResults();
  }

  function clearResults() {
    resultsContainer.innerHTML = '';
    resultsContainer.setAttribute('aria-busy', 'false');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
